import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import PropTypes from 'prop-types';
import Select from 'react-select';
import ptBR from 'date-fns/locale/pt-BR';
import DatePicker, { registerLocale } from 'react-datepicker';
import InputMask from 'react-input-mask';
/* actions */
import { addVaccines } from '../../../redux/actions/vaccines';
/* components */
import ErrorMsgField from '../../../components/ErrorMsgField';
import AlertMsg from '../../../components/AlertMsg';
import FormFooter from '../../../components/FormFooter';
import FormHeader from '../../../components/FormHeader';
import ModalAddVeterinarian from '../../../components/ModalAddVeterinarian';
import ModalAddProduct from '../../../components/ModalAddProduct';
/* form-validation */
import schema from '../../../form-validation/vaccine';
/* utils */
import {
  customStyle,
  customThemes,
  customFilter,
  firstOption,
  handleChangeSelect
} from '../../../utils/select';
import sortable from '../../../utils/sortable';
import handleChangeDefault from '../../../utils/handleChangeDefault';
registerLocale('ptBR', ptBR);

function Add (props) {
  const { handleModalClose } = props;
  const dispatch = useDispatch();
  const submitButton = useRef();
  const { register, handleSubmit, errors, reset, setError, setValue } = useForm(
    {
      validationSchema: schema,
      reValidateMode: 'onSubmit'
    }
  );
  const sessionUser = useSelector(state => state.authentications.user);
  const company = sessionUser.company._id;
  const vaccines = useSelector(state => state.vaccines);
  const INITIAL_VALUES = {
    identifier: '',
    expirationDate: new Date(new Date().setHours(0, 0, 0, 0)),
    manufacturingDate: new Date(new Date().setHours(0, 0, 0, 0)),
    vaccinationDate: new Date(new Date().setHours(0, 0, 0, 0)),
    vaccinationHour: new Date().toLocaleTimeString(),
    vaccinationNextDate: new Date(
      new Date(new Date().setMonth(new Date().getMonth() + 12)).setHours(
        0,
        0,
        0,
        0
      )
    ),
    veterinarian: '',
    product: '',
    pet: '',
    description: '',
    company
  };
  const [data, setData] = useState(INITIAL_VALUES);
  const veterinarians = useSelector(state => state.veterinarians.data);
  const [veterinarianOptions, setVeterinarianOptions] = useState([firstOption]);
  const [showModalAddVeterinarian, setShowModalAddVeterinarian] = useState(
    false
  );
  const products = useSelector(state => state.products.data);
  const [productOptions, setProductOptions] = useState([firstOption]);
  const [showModalAddProduct, setShowModalAddProduct] = useState(false);

  useEffect(() => {
    if (props.data) {
      setData({ ...data, ...props.data });
    }
  }, [props.data]);

  useEffect(() => {
    if (veterinarians.length) {
      const options = veterinarians
        .map(veterinarian => ({
          value: veterinarian._id,
          label: `${veterinarian.name}`,
          sortValue: `${veterinarian.name}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setVeterinarianOptions([...options]);
    }
  }, [veterinarians, setVeterinarianOptions]);

  useEffect(() => {
    if (products.length) {
      const options = products
        .map(product => ({
          value: product._id,
          label: `${product.name} | ${product.barCode}`,
          sortValue: `${product.name} | ${product.barCode}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setProductOptions([...options]);
    }
  }, [products, setProductOptions]);

  const submit = () => {
    const resetForm = () => {
      reset();
      setData({ ...INITIAL_VALUES, ...props.data });
    };
    const newData = {
      ...data
    };
    const [hours, minutes] = data.vaccinationHour.split(':');
    newData.vaccinationDate.setHours(
      parseInt(hours, 10),
      parseInt(minutes, 10)
    );
    delete newData.vaccinationHour;
    dispatch(addVaccines(newData, resetForm, submitButton.current));
  };

  return (
    <div
      className="container-fluid"
      style={{
        display: `${
          (showModalAddProduct || showModalAddVeterinarian) && handleModalClose
            ? 'none'
            : 'block'
        }`
      }}
    >
      <ModalAddVeterinarian
        showModal={showModalAddVeterinarian}
        handleModalClose={() => setShowModalAddVeterinarian(false)}
      />
      <ModalAddProduct
        showModal={showModalAddProduct}
        handleModalClose={() => setShowModalAddProduct(false)}
      />
      {vaccines.addSuccess && (
        <AlertMsg type="success" msg="Vacina cadastrada com sucesso" />
      )}
      {vaccines.errorContent &&
        !vaccines.errorContent.message.includes('400') &&
        vaccines.addError && (
        <AlertMsg type="danger" msg="Falha durante cadastro da vacina" />
      )}
      {vaccines.errorContent &&
        vaccines.errorContent.message.includes('400') &&
        vaccines.addError && (
        <AlertMsg
          type="danger"
          msg="Uma vacina com número de partida já está cadastrada"
        />
      )}
      {!vaccines.addSuccess && !vaccines.addError && (
        <AlertMsg type="info" msg="Área cadastro de vacinas" />
      )}
      <FormHeader title="Cadastrar Vacina" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="form-group">
          <div className="form-group form-inline mb-0">
            <label className="col-sm-12 d-inline p-0 mb-2" htmlFor="product">
              Vacina
              <span className="field-required">*</span>
            </label>
            <Select
              name="product"
              className="col p-0"
              ref={register}
              onChange={evt =>
                handleChangeSelect(
                  { ...evt, name: 'product' },
                  data,
                  setData,
                  setError,
                  setValue
                )
              }
              defaultValue={productOptions[0]}
              value={productOptions.find(
                product => product.value === data.product
              )}
              options={productOptions}
              isSearchable={true}
              theme={customThemes}
              styles={customStyle}
              filterOption={customFilter}
              noOptionsMessage={() => 'Nenhum registro encontrado'}
            />
            <div className="pl-2">
              <button
                type="button"
                className="btn btn-success"
                title="Adicionar Produto"
                disabled={showModalAddProduct}
                onClick={() => setShowModalAddProduct(true)}
              >
                <i className="fa fa-plus" />
                &nbsp;Produto
              </button>
            </div>
          </div>
          {errors.product && errors.product.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label>
                N&deg; de Partida<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="identifier"
                name="identifier"
                autoComplete="off"
                value={data.identifier}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.identifier && errors.identifier.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="manufacturingDate">
                Data de Fabricação<span className="field-required">*</span>
              </label>
              <DatePicker
                className="form-control"
                name="manufacturingDate"
                onChange={date => setData({ ...data, manufacturingDate: date })}
                selected={data.manufacturingDate}
                placeholderText="dd/MM/yyyy"
                locale="ptBR"
                dateFormat="dd/MM/yyyy"
                autoComplete="off"
                customInput={
                  <input type="text" date={data.manufacturingDate} />
                }
              />
              {errors.manufacturingDate &&
                errors.manufacturingDate.type === 'date' && (
                <ErrorMsgField type="date" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="expirationDate">
                Data de Vencimento<span className="field-required">*</span>
              </label>
              <DatePicker
                className="form-control"
                name="expirationDate"
                onChange={date => setData({ ...data, expirationDate: date })}
                selected={data.expirationDate}
                placeholderText="dd/MM/yyyy"
                locale="ptBR"
                dateFormat="dd/MM/yyyy"
                autoComplete="off"
                customInput={<input type="text" date={data.expirationDate} />}
              />
              {errors.expirationDate &&
                errors.expirationDate.type === 'date' && (
                <ErrorMsgField type="date" />
              )}
              {errors.expirationDate &&
                errors.expirationDate.type === 'endDate' && (
                <ErrorMsgField type="date" msg="Data vencimento inválida" />
              )}
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="form-group form-inline mb-0">
            <label
              className="col-sm-12 d-inline p-0 mb-2"
              htmlFor="veterinarian"
            >
              Veterinário
              <span className="field-required">*</span>
            </label>
            <Select
              className="col p-0"
              name="veterinarian"
              ref={register}
              onChange={evt =>
                handleChangeSelect(
                  { ...evt, name: 'veterinarian' },
                  data,
                  setData,
                  setError,
                  setValue
                )
              }
              defaultValue={veterinarianOptions[0]}
              value={veterinarianOptions.find(
                veterinarian => veterinarian.value === data.veterinarian
              )}
              options={veterinarianOptions}
              isSearchable={true}
              theme={customThemes}
              styles={customStyle}
              filterOption={customFilter}
              noOptionsMessage={() => 'Nenhum registro encontrado'}
            />
            <div className="pl-2">
              <button
                type="button"
                className="btn btn-success"
                title="Adicionar Veterinário"
                disabled={showModalAddVeterinarian}
                onClick={() => setShowModalAddVeterinarian(true)}
              >
                <i className="fa fa-plus" />
                &nbsp;Veterinário
              </button>
            </div>
          </div>
          {errors.veterinarian && errors.veterinarian.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="row">
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="vaccinationDate">
                Data de Aplicação<span className="field-required">*</span>
              </label>
              <DatePicker
                className="form-control"
                name="vaccinationDate"
                onChange={date => setData({ ...data, vaccinationDate: date })}
                selected={data.vaccinationDate}
                placeholderText="dd/MM/yyyy"
                locale="ptBR"
                dateFormat="dd/MM/yyyy"
                autoComplete="off"
                customInput={<input type="text" date={data.vaccinationDate} />}
              />
              {errors.vaccinationDate &&
                errors.vaccinationDate.type === 'date' && (
                <ErrorMsgField type="date" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="vaccinationHour">
                Hora de Aplicação<span className="field-required">*</span>
              </label>
              <InputMask
                id="vaccinationHour"
                name="vaccinationHour"
                maskChar=""
                mask="99:99"
                placeholder="HH:mm"
                className="form-control"
                autoComplete="off"
                value={data.vaccinationHour}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.vaccinationHour &&
                errors.vaccinationHour.type === 'hour' && (
                <ErrorMsgField type="hour" />
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="vaccinationNextDate">
                Data Próxima Aplicação
                <span className="field-required">*</span>
              </label>
              <DatePicker
                className="form-control"
                name="vaccinationNextDate"
                onChange={date =>
                  setData({ ...data, vaccinationNextDate: date })
                }
                selected={data.vaccinationNextDate}
                placeholderText="dd/MM/yyyy"
                locale="ptBR"
                dateFormat="dd/MM/yyyy"
                autoComplete="off"
                customInput={
                  <input type="text" date={data.vaccinationNextDate} />
                }
              />
              {errors.vaccinationNextDate &&
                errors.vaccinationNextDate.type === 'date' && (
                <ErrorMsgField type="date" />
              )}
              {errors.vaccinationNextDate &&
                errors.vaccinationNextDate.type === 'endDate' && (
                <ErrorMsgField
                  type="date"
                  msg="Data próxima aplicação inválida"
                />
              )}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="description">
            Descrição<span className="field-required">*</span>
          </label>
          <textarea
            className="form-control"
            id="description"
            name="description"
            rows="3"
            value={data.description}
            onChange={evt => handleChangeDefault(evt, data, setData)}
            ref={register}
          ></textarea>
          {errors.description && errors.description.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        {handleModalClose ? (
          <div className="container-fluid p-0">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleModalClose}
            >
              <i className="fa fa-times"></i>
              &nbsp;Fechar
            </button>
            <button
              type="submit"
              ref={submitButton}
              className="btn btn-primary float-right"
            >
              <i className="fa fa-save"></i>
              &nbsp;Salvar
            </button>
          </div>
        ) : (
          <FormFooter url="/vaccines" reference={submitButton} />
        )}
      </form>
    </div>
  );
}

Add.propTypes = {
  handleModalClose: PropTypes.func,
  data: PropTypes.object
};

export default Add;
